import React, { useState, useRef, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router";
import Autosuggest from "react-autosuggest";
import { ReactComponent as SearchIcon } from "../svg/magnifying-glass-searchbar.svg";
import _uniqby from "lodash.uniqby";
import { useBestSearchMatch, useSearchBarPages } from "../hooks/wow/search";
import Failed_to_Load_Image from "images/wow/Failed_to_Load_Image.png";
import { useAutoFocus } from "../hooks/general-hooks";

function escapeRegexCharacters(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function useSuggestions(excludeSections) {
  const {
    data: searchBarPages,
    loading: loadingSearchBarPages,
    error: errorSearchBarPages,
  } = useSearchBarPages({ exclude: excludeSections || [] });

  return (value) => {
    if (!searchBarPages || loadingSearchBarPages || errorSearchBarPages) {
      return [];
    }

    const escapedValue = escapeRegexCharacters(value.trim());
    if (escapedValue === "") {
      return [];
    }

    const regex = new RegExp(escapedValue, "i");
    const suggestions = searchBarPages
      .map((section) => {
        const val = {
          title: section.title,
          pages: _uniqby(
            section.pages.filter((page) => regex.test(page.key)),
            (page) => page.displayName,
          ),
        };

        return val;
      })
      .filter((section) => section.pages.length > 0);

    return suggestions;
  };
}

function getSuggestionValue(suggestion) {
  return suggestion.displayName;
}

function useRenderSuggestion() {
  return (suggestion) => {
    const img = suggestion?.imgKey ? (
      <img src={`https://static.bigbrain.gg/wow/specs/${suggestion.imgKey}.png`} />
    ) : (
      Failed_to_Load_Image
    );
    return (
      <div className="render-suggestion">
        <div>{img}</div>
        <div className="suggest-text">{suggestion.displayName}</div>
      </div>
    );
  };
}

function renderSectionTitle(section) {
  return <strong>{section.title}</strong>;
}

function getSectionSuggestions(section) {
  return section.pages;
}

function getRecentlySearched() {
  if (typeof window !== "undefined") {
    const recentlySearched = localStorage.getItem("recentlySearchedWow");
    if (recentlySearched !== null) {
      return [
        {
          title: "Recently Searched",
          pages: JSON.parse(recentlySearched),
        },
      ];
    }
  }

  return [];
}

export default function Searchbar(props) {
  const {
    placeholder,
    excludeSections = [],
    autoFocus = false,
    className,
    isLandingPage = false,
    setSideNavOpen = () => {},
    setAutoFocus,
  } = props;

  // Browser info
  const history = useHistory();
  const location = useLocation();

  // Input variables
  const inputRef = useAutoFocus({ disable: !autoFocus, setAutoFocus: setAutoFocus });
  const [value, setValue] = useState("");
  const [isInteracting, setInteraction] = useState(false);
  const initInteracting = useRef(false);
  const [lastSearchValue, setLastSearchValue] = useState(undefined);
  const [init, setInit] = useState(false);
  const [highlightedSuggestion, setHighlightedSuggestion] = useState(null);

  // Autosuggest state
  const [suggestions, setSuggestions] = useState(getRecentlySearched());
  const [lastPage, setLastPage] = useState(undefined);

  // Use effect loops, not sure the exact functionality of each
  useEffect(() => {
    setInit(true);
  }, []);

  useEffect(() => {
    if (lastPage && location.pathname !== lastPage && value === lastSearchValue) {
      setValue("");
      setLastPage(undefined);
    }
  }, [location.pathname, lastSearchValue, value]);

  // Autosuggest functions
  const getBestSearchMatch = useBestSearchMatch();
  const getSuggestions = useSuggestions(excludeSections);
  const renderSuggestion = useRenderSuggestion();

  const onChange = (_event, { newValue, method }) => {
    if (method === "type") {
      !isInteracting && setInteraction(true);
      initInteracting.current = true;
    }

    setValue(newValue);
    if (lastSearchValue) {
      setLastSearchValue(undefined);
    }
  };

  const onClick = (e) => {
    !isInteracting && setInteraction(true);
    initInteracting.current = true;
  };

  const onBlur = () => {
    initInteracting.current && !isInteracting && setInteraction(true);
  };

  const onSuggestionsFetchRequested = ({ value, _reason }) => {
    let suggestions = getSuggestions(value);
    if (value.trim().length === 0) {
      suggestions = getRecentlySearched();
    }
    setSuggestions(suggestions);
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const onSuggestionHighlighted = ({ suggestion }) => {
    setHighlightedSuggestion(suggestion);
  };

  const onSuggestionSelected = (event, { suggestion, suggestionValue, method }) => {
    if (suggestionValue && (method === "enter" || method === "click")) {
      if ("targetHref" in suggestion && suggestion.targetHref) {
        return window && window.open(suggestion.url);
      } else if (suggestion.url) {
        history.push({
          pathname: suggestion.url,
          search: "",
        });
      } else {
        search(suggestionValue);
      }
      inputRef && inputRef.current.blur();
      setSideNavOpen(false);
    }
  };

  const changeUrl = (page, lastSearchValue) => {
    if ("targetHref" in page && page.targetHref) {
      window && window.open(page.url);
    } else if (page.url) {
      setLastSearchValue(lastSearchValue);
      setLastPage(page.url);
      if (location.pathname !== page.url) {
        window && window.scrollTo(0, 0);
        history.push({
          pathname: page.url,
        });
        setSideNavOpen(false);
      }
    }
  };

  const search = (searchValue) => {
    if (searchValue.trim() === "") {
      return;
    }

    const matches = getBestSearchMatch(searchValue);
    const nameMatches = matches.filter((match) => !("isNickname" in match.item) || !match.item.isNickname);

    // Check exact name
    const hasExactMatch = matches.length > 0 && Math.round(matches[0].score * 1000000) / 1000000 === 0;

    // Check exact nickname
    const nameMatch = nameMatches.length > 0 && nameMatches[0];
    let resToUse = hasExactMatch && matches[0];

    // Check name substring match
    if (nameMatch) {
      const strippedMatchValue = nameMatch.item.key.toLowerCase();
      const strippedSearchValue = searchValue.toLowerCase();
      if (
        strippedMatchValue.indexOf(strippedSearchValue) === 0 &&
        "isDefaultPage" in nameMatch.item &&
        nameMatch.item.isDefaultPage === true
      ) {
        resToUse = nameMatch;
      }
    }

    // If no exact match, get top suggestion
    if (!resToUse && matches.length > 0 && suggestions[0].pages.length > 0) {
      resToUse = matches.filter((match) => match.item.key === suggestions[0].pages[0].key)[0];
    }

    if (resToUse) {
      changeUrl(resToUse.item, searchValue);

      // Add to recently searched
      if (window) {
        const recentlySearched = localStorage.getItem("recentlySearchedWow");
        const newEntry = resToUse.item;
        if (recentlySearched === null) {
          localStorage.setItem("recentlySearchedWow", JSON.stringify([newEntry]));
        } else {
          const parsed = JSON.parse(recentlySearched);
          const filtered = parsed.filter((item) => item.displayName !== resToUse.item.displayName);
          filtered.unshift(newEntry);
          localStorage.setItem("recentlySearchedWow", JSON.stringify(filtered.slice(0, 6)));
        }
      }
    } else {
      // setSearchResults(matches);
      setLastSearchValue(searchValue);
      // setSearching(true);
    }
  };

  // Handling enter keypress
  const handleKeyPress = useCallback(
    (e) => {
      let searchBar = inputRef && inputRef.current;

      if (e.which === 13) {
        // on enter
        const val = searchBar && searchBar.value;
        if (!val) return;

        if (highlightedSuggestion && highlightedSuggestion.url) {
          if (highlightedSuggestion.targetHref) {
            return window && window.open(highlightedSuggestion.url);
          } else {
            return history.push({
              pathname: highlightedSuggestion.url,
            });
          }
        }

        search(val);
        searchBar.blur();
      }
    },
    [search, highlightedSuggestion],
  );

  useEffect(() => {
    if (init) {
      if (inputRef && inputRef.current) {
        inputRef.current.addEventListener("keypress", handleKeyPress);

        return () => {
          if (inputRef && inputRef.current) {
            inputRef.current.removeEventListener("keypress", handleKeyPress);
          }
        };
      }
    }
  }, [init, handleKeyPress]);

  const searchInputProps = {
    ref: inputRef,
    placeholder: placeholder || "Search Class and Spec",
    name: "query",
    value,
    onChange,
    spellCheck: false,
    id: "super-search-bar",
    onBlur,
    onClick,
  };

  return (
    <div className={`${className}`}>
      <div className="masthead-ugg-search-bar autosuggest-container">
        <Autosuggest
          multiSection
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          renderSectionTitle={renderSectionTitle}
          getSectionSuggestions={getSectionSuggestions}
          onSuggestionSelected={onSuggestionSelected}
          onSuggestionHighlighted={onSuggestionHighlighted}
          shouldRenderSuggestions={() => isInteracting}
          focusInputOnSuggestionClick={false}
          inputProps={searchInputProps}
        />
        <div className="autosuggest-button">
          <SearchIcon />
        </div>
        {false && (
          <div className="spinthatshit-loader">
            <div className="spinner" />
          </div>
        )}
      </div>
    </div>
  );
}
